/* Серверный режим «Говорящего кружка» (who: circle, lipsync) на движке шаблона (план вехи 3 §0 D1, план кружка §2).
   createApp(createCircleServer) (features/app/engine.ts) отдаёт сюда крючки экрана, а каждая вставка SV спрашивает этот объект (SRV):
   цена, готовность, кто говорит, голос, файлы, создание и хвост — только из сервиса заказа кадра (OrderService привязки EngineFrame).
   Правила:
   - одна привязка — один кадр ServerStudio (ключ кадра и его сервис заказа): у другого кадра — и у нового сервиса под прежним ключом —
     своё S, свой мост загрузок, свой хвост BotRun (фасад TAIL пересоздаёт BotRun при смене кадра); файлы, согласие и заказ одного приложения
     или владельца не видны и не уходят под другим;
   - движок не пишет в sessionStorage и не зовёт создание сам: заявку, ключ, отправку, опрос и отказы ведёт сервис (run-driver.ts); при открытии
     ничего не отправляется, только показ;
   - S → входы: одна точка — syncFoot движка (commit, inputs-mirror: под замком не пишет, равные входы не пишет); входы → S — только когда
     входы сменились не зеркалом (повтор из «Истории», «Изменить» после перезагрузки, заявка родной формы): circle-inputs.viewOf → applyView;
     id загрузок пишет мост загрузок, зеркало их только снимает, если файл не того, кто говорит;
   - файл сначала идёт серверу (admit): политика поля формы, лист согласия, одна очередь загрузок (media-bridge); только принятый файл
     читает шаблон (кадр, длительность, обложка) и показывает рамка; превью повтора — фото и запись с сервера, видео — загрузить заново (Q7);
   - голос: «Голос персонажа» — голос карточки персонажа по умолчанию; у фото, близкого, «Вы» и видео голоса по умолчанию нет (Q-C1,
     «Выберите голос»); выбор голоса подтверждает сервер (voice-pick), у записи и своей речи — только с переозвучкой (is_s2s);
   - суммы на странице заказа — только из заказа (bot-run-server: receipt, failText); цена на кнопке — для показа, в заказ уходит цена
     сервиса; скидка — только если форма её публикует;
   - тексты: свои строки адаптера — словарём профиля (uiText, uiFormat), ядро — через ui.tx (текстовый шов BotCore.tx, L-TX). */
import { endpoints } from '../../api';
import type { AvatarDetails } from '../../api/avatar-catalog';
import type { Voice } from '../../api/types';
import { avatarThumb, portraitOrb, voiceOrb } from '../../bridge/voice-orb';
import { accountLocale, uiFormat, uiText } from '../../i18n';
import { circleBalance, forgetCircleBalance, rememberCircleBalance, toolPrice, unitWords } from '../studio/circle/tool-helpers';
import { tailCurrent, tailSteps } from '../studio/circle/order-tail-model';
import { circleEntry } from '../studio/circle/circle-entry';
import {
  circleFormatChoices,
  circleKeys,
  circleLoopOn,
  circleMissing,
  circleMotionOn,
  circlePolicy,
  circleSfxOn,
  circleSources,
  circleTextBounds,
  circleVizChoices,
  circleOccasions,
} from '../studio/circle/circle-model';
import { toolField } from '../studio/circle/tool-form';
import { studioCheapOfferAt, type StudioApp, type StudioForm } from '../studio/studio-api';
import { failText, receipt, serverFoot, type BotRunApi, type BotRunRun } from '../studio/engine-kit/bot-run-server';
import { openConsentSheet, type ConsentSheet } from '../studio/engine-kit/consent-sheet';
import { ENGINES, type EngineBinding, type EngineBound, type EngineScreen } from '../studio/engine-kit/engine-host';
import { createInputsMirror, sameInputs, type InputsMirror } from '../studio/engine-kit/inputs-mirror';
import { createMediaBridge, mediaPolicy, type MediaBridge, type MediaItem } from '../studio/engine-kit/media-bridge';
import { uploadFailureText, type Intent, type OrderService } from '../studio/engine-kit/order-service';
import { peopleModule, personRef, selfPicture, selfRef, SILHOUETTE, watchPeople, type PeopleModule } from '../studio/engine-kit/people-bridge';
import { RESULT_FRAMES, createResultMedia, type ResultMedia } from '../studio/engine-kit/result-media';
import { createRunDriver, peekIntent, type RunDriver } from '../studio/engine-kit/run-driver';
import { poorBalance, serverUi, type ServerUi } from '../studio/engine-kit/server-ui';
import { chatClick, chatFoot, doneP, openSettings } from '../studio/engine-kit/tail-done';
import { checkFailed } from '../studio/engine-kit/upload-checks';
import { details, detailsKey, leaseSkins, loadDetails, skinUrl } from '../studio/engine-kit/char-cards';
import { createVoicePick, type VoicePick } from '../studio/engine-kit/voice-pick';
import { applyView, defaultFace, inputsOf, speechOf, viewFromEngine, viewOf, type CircleView, type CircleViewHooks } from './circle-inputs';

/* ---------- контракт с движком (features/app/engine.ts, serve({...})) ---------- */

type Kind = 'photo' | 'video' | 'audio';
type Inputs = Record<string, string>;
/** Файл в S шаблона: снимок, ролик или запись ({name, size, url, dur, poster, orig, crop, trim, mute, noface}) */
export interface EngineFile {
  name: string;
  size: number;
  url: string;
  dur: number;
  kind?: string;
  poster?: string;
  video?: string | null;
  orig?: { url: string; w: number; h: number } | null;
  w?: number;
  h?: number;
  crop?: { cx: number; cy: number; z: number };
  trim?: { from: number; to: number };
  mute?: boolean;
  noface?: boolean;
  rec?: boolean;
  peaks?: number[];
  [key: string]: unknown;
}
/** Настройки страницы (BotCore.uiDefaults и init каркаса): то, что читает SRV */
export interface EngineUi {
  buzz(kind?: string): void;
  tip(el: Element | null, text: string): void;
  voices(): unknown[];
  myVoices(): unknown[];
  chars?: unknown[];
  [key: string]: unknown;
}
/** Рамка загрузки BotCore.upload экрана (UPH, UPV, UPA) */
interface EngineUpload {
  o: { mb?: number; [key: string]: unknown };
  say(text: string): void;
  paint(): boolean;
}
/** Подвал BotCore.foot экрана (FOOT) */
interface EngineFoot {
  poor(): boolean;
  poorOpen(): void;
  sync(): void;
  miss(): string;
}
/** Карточка «Ещё настройки» (BotCore.settingsCard) */
interface EngineCard {
  sync(): void;
  close(): void;
  readonly isOpen?: boolean;
}
/** Блок «Кто говорит» (BotPeople.attach) */
interface EnginePeople {
  setIn(on: boolean): void;
  inFrame(): boolean;
  paintForm(): void;
  close(): void;
}
/** Крючки, которые движок отдаёт serve (engine.ts, блок SV после cropCancel): части экрана ниже по файлу — геттеры */
export interface CircleEngineHooks extends CircleViewHooks {
  S: CircleViewHooks['S'] & {
    files: { photo: EngineFile | null; video: EngineFile | null };
    aud: EngineFile | null;
    crop: unknown;
    cheap: boolean;
    app: { id?: number; n: string; k?: number; f?: string; ch?: number } | null;
    card: boolean;
  };
  REC: { st: string; sec: number; want: boolean; sim: boolean };
  CAM: { st: string; sim: boolean };
  lists: {
    OCC: Array<[string, string]>;
    SFX: Array<[string, string]>;
    VIZ: Array<[string, string, string]>;
    SKINS: Array<[string, string, string]>;
    FMT: Array<[string, string, string]>;
    FMT_X: [string, string, string];
    MV_DO: Array<[string, string]>;
    MV_NOT: Array<[string, string]>;
  };
  readonly P: EnginePeople;
  readonly tail: BotRunApi;
  readonly foot: EngineFoot;
  readonly sc: EngineCard;
  readonly up: Record<Kind, EngineUpload>;
  root(): HTMLElement | null;
  mounted(): boolean;
  ui(): EngineUi;
  ic(name: string): string;
  esc(value: string): string;
  char(): { id: number | string; name?: string; img?: string; y?: number; voices?: unknown[] };
  person(): { id: number; n: string } | null;
  personPh(): unknown;
  fileOf(kind: string): EngineFile | null;
  speechAud(): boolean;
  speechText(): boolean;
  dur(): number;
  charVoice(): boolean;
  mute(c: string): boolean;
  setLimits(o: { min?: number; circle?: number; video?: number; rec?: number; sfx?: number; mv?: number }): void;
  setup(o: unknown): void;
  apply(o: unknown): void;
  whoSet(w: string, ch?: unknown): void;
  paintPage(): void;
  putMain(anim: boolean): void;
  putTx(anim: boolean): void;
  syncSet(): void;
  syncStage(): void;
  fileChanged(): void;
  audPaint(): void;
  whoChanged(): void;
  voiceChanged(): void;
  txChanged(focus?: boolean): void;
  styChanged(): void;
  whoSync(): void;
  readFile(file: File, kind: 'photo' | 'video'): void;
  readAud(file: File): void;
  stopLive(): void;
  voicePick(): void;
  camStop(): void;
  recCancel(): void;
  runSteps(): string[][];
  heroPic(A: number, g?: string): string;
}
/** Голос в виде шаблона (строки слота «Голос», каталог страницы): шар — строка CSS; is_s2s — строго с сервера */
export interface EngineVoice {
  id: number;
  name: string;
  orb: string;
  tags: string[];
  sex: string | null;
  c: string[];
  mine: boolean;
  is_s2s: boolean;
  preview_url: string | null;
  st?: string;
}

export interface CircleServer {
  ui<U extends object>(base: U): ServerUi<U>;
  blank(): string;
  entry(o: unknown): Record<string, unknown>;
  open(o: unknown): void;
  close(): void;
  tail(create: (o: Record<string, unknown>) => BotRunApi, opts: Record<string, unknown>): BotRunApi;
  wrap<T>(slot: 'cheap', value: T): T;
  has(part: 'char' | 'photo' | 'video' | 'sty' | 'skin' | 'loop' | 'motion' | 'sfx'): boolean;
  missing(): string | null;
  ready(): string;
  label(): string | null;
  price(cheap: boolean): number;
  busy(): boolean;
  unit(round: boolean): string;
  badge(round: boolean, o: { cls: string; id: string }): string;
  upOpts(kind: Kind): Record<string, unknown>;
  note(kind: Kind): string;
  admit(file: File, kind: Kind): boolean;
  accept(f: EngineFile, kind: Kind): void;
  remove(kind: Kind): void;
  canRecord(): boolean;
  micText(kind: 'off' | 'denied'): string;
  camText(): string;
  recorded(x: { sec: number; blob: Blob | null; url: string | null }): void;
  voice(id: unknown): EngineVoice | null;
  defVoice(): number | null;
  charS2s(): boolean;
  voiceHint(c: string | null): string;
  /** Выбрать голос через сервер; обещание — исход (true — голос стал голосом экрана); более позднее нажатие отменяет ждущее */
  pickVoice(id: unknown, c: string | null): Promise<boolean>;
  /** Отменить ждущий выбор голоса (выбрана строка без голоса: «Как в записи», «Оставить мою речь») */
  dropPick(): void;
  charImg(c: { id?: unknown; img?: string }, skin?: [string, string, string] | null): string;
  meUrl(): string;
  commit(): void;
  start(btn: HTMLElement): void;
  steps(): string[][];
  snap(): Record<string, unknown>;
  sentPrice(): number;
  drive(r: BotRunRun): void;
  failText(r: BotRunRun): string | null;
  receipt(r: BotRunRun): string;
  click(d: DOMStringMap, el: HTMLElement, r: BotRunRun | null): boolean;
  leave(go: () => void, focus: boolean): void;
  save(btn: HTMLElement): void;
}

/* ---------- общие части ---------- */

interface Core {
  html: { unit(n: number, rest: string): string };
  priceBadge(n: number, rest: string, o: { cls?: string; id?: string }): string;
  saveBtn(btn: HTMLElement, make: () => Promise<{ data: Blob; name: string }>, o: { buzz(): void; tip(el: Element, text: string): void }): Promise<unknown>;
}
const core = () => window.BotCore as unknown as Core;
/** «Занята» кнопка шаблона (SteosUI.busy): крутилка, пока идёт действие */
function busyButton<T>(btn: HTMLElement | null, fn: () => Promise<T>): Promise<T | undefined> {
  const busy = window.SteosUI?.busy;
  return btn && busy ? busy(btn, fn) : fn();
}
/** accept рамок BotCore.upload (их проверка типа — до моста): любой файл вида и расширения без типа у браузера; точные типы и вес —
    у моста по политике поля формы. Строка — при создании рамки, поэтому широкая */
const ACCEPT: Record<Kind, string> = {
  photo: 'image/*,.jpg,.jpeg,.png,.webp,.heic,.heif',
  video: 'video/*,.mp4,.m4v,.mov,.qt,.webm',
  audio: 'audio/*,.m4a,.mp3,.wav,.ogg,.oga,.opus,.webm,.aac,.flac',
};
const TRANSPORT = uploadFailureText(null);
const FORM_SAY = [
  'Некоторые файлы повтора больше недоступны. Загрузите их заново.',
  'Этот инструмент сейчас недоступен. Попробуйте обновить форму.',
  'Инструмент обновляется. Обновите форму позже.',
];
const num = (value: unknown): number | null => {
  const n = typeof value === 'string' && /^[1-9][0-9]*$/.test(value) ? Number(value) : value;
  return typeof n === 'number' && Number.isSafeInteger(n) && n > 0 ? n : null;
};
const locale = () => (accountLocale() === 'ru' ? 'ru' : 'en');
function engineVoice(voice: Voice): EngineVoice {
  return {
    id: voice.id,
    name: voice.name,
    orb: voiceOrb(voice) ?? '',
    tags: Array.isArray(voice.tags) ? voice.tags : [],
    sex: voice.sex ?? null,
    c: [],
    mine: !!voice.is_mine,
    is_s2s: voice.is_s2s === true,
    preview_url: voice.preview_url ?? null,
  };
}
/** Голос каталога оболочки (ui.voices / ui.myVoices: server-state.ts shellVoice) в виде шаблона; is_s2s — только явное true */
function shellVoice(raw: unknown): EngineVoice | null {
  const v = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  const id = num(v?.id);
  if (!v || id === null) return null;
  return {
    id,
    name: typeof v.name === 'string' ? v.name : '',
    orb: typeof v.orb === 'string' ? v.orb : '',
    tags: Array.isArray(v.tags) ? v.tags.filter((tag): tag is string => typeof tag === 'string') : [],
    sex: typeof v.sex === 'string' ? v.sex : null,
    c: Array.isArray(v.c) ? v.c.filter((x): x is string => typeof x === 'string') : [],
    mine: v.mine === true || v.is_mine === true,
    is_s2s: v.is_s2s === true,
    preview_url: typeof v.preview_url === 'string' ? v.preview_url : null,
    ...(typeof v.st === 'string' ? { st: v.st } : {}),
  };
}

/* ---------- пресеты звука (общие для кадров; язык — в ключе). Карточки персонажей и картинки видов — engine-kit/char-cards (общие с рилсом) ---------- */

const sfxPresets = new Map<string, Array<[string, string]> | null>();
const sfxFlight = new Map<string, Promise<void>>();

/** Состояние одной привязки (кадра ServerStudio) */
interface Frame {
  b: EngineBinding;
  key: string;
  svc: OrderService;
  app: StudioApp;
  bridge: MediaBridge;
  driver: RunDriver;
  mirror: InputsMirror;
  /** Форма, для которой прошла первая сверка (пределы, списки, входы → S) */
  form: StudioForm | null;
  /** Входы без id загрузок, которые S уже знает (своя запись зеркала или перенос в S) */
  known: Inputs | null;
  /** Вход экрана (контракт apply шаблона) и что он решает сам: кто говорит своё фото, видео или аудио; формат */
  entry: Record<string, unknown>;
  keepWho: boolean;
  keepFormat: boolean;
  keepSpeech: boolean;
  /** Ключ заявки, входы которой уже в S */
  intentSeen: string;
  /** Первая форма пришла, сверка ждёт конца прохода сервиса; formAt — форма, проход которой закончен (formSync) */
  formWait: boolean;
  formAt: StudioForm | null;
  /** Входы первой сверки (подстановка входа экрана и повтора) без id загрузок */
  prefill: Inputs | null;
  /** Файлы, которые мост принял: только их читает шаблон (admit) */
  approved: WeakSet<File>;
  /** Запись моста, файл которой сейчас в рамке вида */
  items: Partial<Record<Kind, MediaItem>>;
  /** Адреса blob, созданные шаблоном для файлов этого кадра */
  urls: Set<string>;
  /** Превью какой загрузки читается по виду */
  restoring: Partial<Record<Kind, string>>;
  /** Кадр и фрагмент из входов для файла, который ещё восстанавливается */
  pending: Partial<Pick<CircleView, 'crop' | 'trim'>>;
  /** Ошибка формы, уже сказанная в рамке */
  said: string;
  consent: ConsentSheet | null;
  pick: VoicePick;
  /** Голоса, подтверждённые сервером в этом кадре */
  voices: Map<number, EngineVoice>;
  peek: Intent | null;
  formShown: boolean;
  balanceAsk: boolean;
  orderSeen: number | undefined;
  /** Номер записи с микрофона («Запись 1») */
  rec: number;
  /** Итог заказа: проигрывание в рамке хвоста и «Скачать» (result-media) */
  media: ResultMedia;
}

/* ---------- сервер экрана ---------- */

let instance: { bind(b: EngineBinding): EngineBound } | null = null;

export function createCircleServer(h: CircleEngineHooks): CircleServer {
  const { S } = h;
  /** Вид по умолчанию — первый вид шаблона (SKINS: ключ def, без фильтра): у сервера это отсутствие skin_id, его название — шаблона */
  const SKIN0 = [...h.lists.SKINS[0]] as [string, string, string];
  /** Начальное S шаблона (свойства-значения; зеркала src, drive, words — геттеры): новый кадр начинает с него */
  const S0 = JSON.stringify(
    Object.fromEntries(Object.entries(Object.getOwnPropertyDescriptors(S)).filter(([, d]) => 'value' in d).map(([k, d]) => [k, d.value])),
  );
  /* списки шаблона — демо-данные: без формы сервера звуков и видов нет (их дают форма и карточка персонажа) */
  h.lists.SFX.splice(0);
  h.lists.SKINS.splice(1);
  let frame: Frame | null = null;
  let bound: Frame | null = null;
  /** Кадр, к которому относятся S и экземпляр хвоста BotRun (объект, а не ключ: ключи повторяются после нового входа в раздел) */
  let painted: Frame | null = null;
  let tailOpts: { create: (o: Record<string, unknown>) => BotRunApi; opts: Record<string, unknown> } | null = null;
  let inst: BotRunApi | null = null;
  /** Идёт смена страницы start хвоста (BotRun рисует заказ по таймеру swap) */
  let startSwap = false;
  /** Номер последнего выбора голоса (pickVoice): ответ на прежний не применяется */
  let pickSeq = 0;
  const facade = {} as BotRunApi;

  const cur = () => (bound && bound === frame ? frame : null);
  const live = (f: Frame) => {
    const s = f.svc.get();
    return { s, d: f.svc.derive(s) };
  };
  const onForm = () => h.mounted() && !h.tail.run;
  const keysOf = (f: Frame | null) => circleKeys(f?.svc.get().form);
  const keyOf = (f: Frame, kind: Kind) => {
    const keys = keysOf(f);
    return kind === 'photo' ? keys.photo : kind === 'video' ? keys.video : keys.audio;
  };
  const kindOfKey = (f: Frame, key: string): Kind | null => (['photo', 'video', 'audio'] as const).find(kind => keyOf(f, kind) === key) ?? null;
  /** Входы без id загрузок: их пишет мост, переносить в S нечего */
  const strip = (f: Frame, inputs: Inputs): Inputs => {
    const out = { ...inputs };
    for (const kind of ['photo', 'video', 'audio'] as const) {
      const key = keyOf(f, kind);
      if (key) delete out[key];
    }
    return out;
  };
  /** Подсказка-стекло у элемента; элемента нет на месте — у кнопки подвала */
  const tip = (el: Element | null, text: string) => {
    const at = el?.isConnected ? el : (h.root()?.querySelector('.ap-foot .btn') ?? null);
    if (text && at) h.ui().tip(at, uiText(text));
  };
  const intentOf = (f: Frame | null): Intent | null => {
    const s = f?.svc.get();
    if (!f || !s) return null;
    return s.intent ?? (!s.identity && !s.integrityError ? f.peek : null);
  };
  /** Голос персонажа, который говорит, по умолчанию: карточка персонажа (default_voice_id, иначе первый голос), пока её нет — первый голос ряда */
  function charVoiceId(): number | null {
    if (h.who() !== 'char') return null;
    const c = h.char();
    const id = num(c.id);
    const card = id ? details.get(detailsKey(id)) : undefined;
    if (card) return card.avatar.default_voice_id ?? card.avatar.voice_ids[0] ?? null;
    return num(Array.isArray(c.voices) ? c.voices[0] : null);
  }
  /** id готовых загрузок файлов в S (рамки и ряд): тот, кто говорит, снова свой файл — id снова во входах */
  const uploadsOf = (f: Frame): CircleView['uploads'] => {
    const out: CircleView['uploads'] = {};
    for (const kind of ['photo', 'video', 'audio'] as const) {
      const item = f.items[kind];
      if (item?.state === 'ready' && item.uploadId && held(kind)) out[kind] = item.uploadId;
    }
    return out;
  };
  const view = (f: Frame) => viewFromEngine(h, charVoiceId(), uploadsOf(f));

  /* ---- голоса ---- */
  function voice(id: unknown): EngineVoice | null {
    const want = num(id);
    if (want === null) return null;
    const f = cur() ?? frame;
    /* имя на языке профиля — из строки каталога карточки персонажа: голос, подтверждённый сервером (voice-pick, голос формы), приходит
       с именем из сервиса голосов по-английски («Pensive Sorceress» вместо «Задумчивая чародейка») */
    const local = (v: EngineVoice): EngineVoice => {
      for (const card of details.values()) {
        const row = card?.voice_cards?.find(item => item.id === want);
        if (row?.name) return { ...v, name: row.name };
      }
      return v;
    };
    const picked = f?.voices.get(want);
    if (picked) return local(picked);
    const own = f?.svc.get().voice;
    if (own && own.id === want) return local(engineVoice(own));
    const ui = h.ui();
    for (const list of [ui.voices?.() ?? [], ui.myVoices?.() ?? []])
      for (const raw of list) {
        const v = shellVoice(raw);
        if (v && v.id === want) return v;
      }
    for (const card of details.values()) {
      /* строка каталога из карточки — имя на языке профиля («Задумчивая чародейка»); voices карточки — имя голоса из сервиса (английское) */
      const row = card?.voice_cards?.find(item => item.id === want);
      if (row) return engineVoice(row);
      const v = card?.voices.find(item => item.id === want);
      if (v)
        return {
          id: v.id,
          name: v.name,
          orb: portraitOrb(avatarThumb(v)) ?? '',
          tags: [],
          sex: card!.avatar.sex ?? null,
          c: [],
          mine: false,
          is_s2s: v.can_s2s === true,
          preview_url: v.preview_url ?? null,
        };
    }
    return null;
  }

  /* ---- готовность ---- */
  function missing(): string | null {
    const f = cur();
    if (!f) return uiText('Проверяем доступность');
    const { s, d } = live(f);
    if (s.intent && !d.orderId) return '';
    if (!s.identity && !s.integrityError) return uiText('Проверяем доступность');
    if (s.integrityError) return uiText(s.integrityError);
    if (!s.identity || s.identity.appId !== f.app.id) return uiText('Проверяем доступность');
    /* форма не загрузилась или устарела: кнопка «Обновить форму» (recovery) */
    if (!s.form) return s.error ? '' : uiText('Проверяем доступность');
    if (s.versionStale) return '';
    const sources = circleSources(s.form);
    if (!Object.values(sources).some(Boolean)) return uiText(s.error || 'Этот инструмент сейчас недоступен. Попробуйте обновить форму.');
    if (!s.form.available) return uiText('Создание сейчас недоступно');
    return null;
  }
  function ready(): string {
    const f = cur();
    if (!f) return uiText('Проверяем доступность');
    const { s } = live(f);
    /* неподтверждённая загрузка: «Продолжить загрузку» (recovery) — кнопка активна; иначе — загрузка идёт */
    if (f.bridge.pending()) return f.bridge.stalled() && s.error === TRANSPORT && !s.busy ? '' : uiText('Загружаем файл');
    if (!s.form) return '';
    const who = h.who();
    if (who === 'person') {
      const ref = personRef(S.ppl[0]?.id);
      if (!ref.ok && ref.reason !== 'photos') return ref.text;
    }
    if (who === 'me' && !selfRef()) return uiText('Добавьте свои фото');
    const sp = speechOf({ who, speech: S.speech === 'audio' ? 'audio' : 'text', keep: h.keepOn() });
    const miss = circleMissing(s.form, s.inputs, true, sp === 'own' ? 'original' : sp, {
      pick: who === 'photo' || who === 'video' || who === 'audio' ? who : null,
      cropping: !!S.crop,
    });
    return miss ? uiText(miss) : '';
  }
  /** Что починить вместо создания: форма не загрузилась или устарела — перечитать; загрузка не подтверждена — продолжить тем же id */
  function recovery(f: Frame): ((btn: HTMLElement) => void) | null {
    const { s } = live(f);
    if ((!s.form && s.error && !s.integrityError) || s.versionStale) return () => f.svc.setters.revision(value => value + 1);
    if (f.bridge.stalled() && !s.busy && s.error === TRANSPORT) return btn => void busyButton(btn, () => f.bridge.resume());
    return null;
  }
  /** Цена за единицу: цена формата, которую публикует поле формата формы (prices: кружок и пропорции видео), иначе app.price (ToolPrice),
      иначе цена формы у платной работы; null — неизвестна */
  function unitPrice(): number | null {
    const f = cur() ?? frame;
    const form = f?.svc.get().form;
    const key = form ? circleKeys(form).format : undefined;
    const own = key ? toolField(form, key)?.prices?.[h.round() ? 'circle' : S.ar] : undefined;
    if (typeof own === 'number' && Number.isSafeInteger(own) && own > 0) return own;
    const amount = toolPrice(f?.app.price).amount;
    if (amount !== null && amount > 0) return amount;
    const s = f?.svc.get();
    if (!f || !s?.form || s.form.chargeable === false) return null;
    const price = f.svc.derive(s).price;
    return Number.isFinite(price) && price > 0 ? price : null;
  }
  const unitText = (round: boolean) => {
    const f = cur() ?? frame;
    return uiText(unitWords(toolPrice(f?.app.price).unit, round ? 'за кружок' : 'за видео'));
  };

  /* ---- входы ↔ S ---- */
  /** S → входы (syncFoot): под замком и на хвосте не пишется; равные входы — тот же объект */
  function commit(f: Frame | null = cur()) {
    if (!f || !f.form || !h.mounted() || h.tail.run) return;
    const s = f.svc.get();
    if (!s.form || f.svc.derive(s).locked) return;
    const v = view(f);
    f.mirror.commit(prev => inputsOf(s.form, v, prev));
    f.known = strip(f, f.svc.get().inputs);
  }
  /** Входы → S: перенести вид и нарисовать форму; keep — что решил вход экрана */
  function adopt(f: Frame, inputs: Inputs, keep: { who?: boolean; format?: boolean; speech?: boolean } = {}, first = false) {
    const form = f.svc.get().form;
    const v = viewOf(form, inputs);
    /* первая сверка: входы не знают, кто говорит, — герой формы по умолчанию (шаблон-кружок: его персонаж) */
    if (first && !v.who) Object.assign(v, defaultFace(form) ?? {});
    applyView(h, v, keep);
    if (v.voice === undefined && v.vm === undefined && !(h.vctx() && S.vm[h.vctx()!] === 'voice')) S.voice = h.who() === 'char' ? charVoiceId() : null;
    f.pending = { ...(v.crop ? { crop: v.crop } : {}), ...(v.trim ? { trim: v.trim } : {}) };
    for (const kind of ['photo', 'video'] as const) {
      const file = S.files[kind];
      if (file && f.items[kind]?.uploadId && f.items[kind]?.uploadId === inputs[keyOf(f, kind) ?? '']) placeOn(f, kind, file);
    }
    f.known = strip(f, inputs);
    f.mirror.seen(inputs);
    h.whoSync();
  }
  /** Кадр и фрагмент входов — на файл, который пришёл (превью повтора) */
  function placeOn(f: Frame, kind: 'photo' | 'video', file: EngineFile) {
    if (f.pending.crop) file.crop = { ...f.pending.crop };
    if (kind === 'video' && f.pending.trim) file.trim = { from: f.pending.trim.start / 1000, to: f.pending.trim.end / 1000 };
  }

  /* ---- сверка формы с сервисом (каждый зафиксированный вид) ---- */
  /** Списки и пределы формы на экране (новая форма с теми же списками форму не перерисовывает) */
  const listsKey = () => JSON.stringify([h.lists.FMT, h.lists.VIZ, h.lists.OCC, h.limits()]);
  /** Идёт смена страницы шаблона (SteosUI.swap): форму нарисует она */
  const swapping = () => !!h.root()?.classList.contains('leave');
  function lists(f: Frame, form: StudioForm) {
    const L = h.lists;
    const fmt = circleFormatChoices(form, h.round() ? 'circle' : S.ar).filter(([value]) => value !== '4:5');
    if (fmt.length) L.FMT.splice(0, L.FMT.length, ...fmt.map(([value]) => L.FMT.find(item => item[0] === value) ?? (value === L.FMT_X[0] ? L.FMT_X : ([value, value, ''] as [string, string, string]))));
    const viz = circleVizChoices(form);
    L.VIZ.splice(0, L.VIZ.length, ...viz);
    if (viz.length && !viz.some(item => item[0] === S.viz)) S.viz = viz[0][0];
    L.OCC.splice(0, L.OCC.length, ...circleOccasions(form));
    const keys = circleKeys(form);
    const policy = circlePolicy(form);
    const bounds = circleTextBounds(form, { format: 'circle' });
    const video = circleTextBounds(form, { format: '9:16' });
    const sfx = keys.sfx ? Math.min(policy?.sfx_max_length ?? 120, toolField(form, keys.sfx)?.max_length ?? 120) : 0;
    const mv = toolField(form, keys.motion ?? keys.movement ?? '')?.max_length ?? 0;
    h.setLimits({
      min: Math.max(1, bounds.min),
      circle: bounds.max,
      video: video.max,
      rec: (policy?.max_duration_ms ?? 60000) / 1000,
      ...(sfx > 0 ? { sfx } : {}),
      ...(mv > 0 ? { mv } : {}),
    });
    for (const kind of ['photo', 'video', 'audio'] as const) {
      const key = keyOf(f, kind);
      const p = key ? mediaPolicy(form, key) : null;
      if (p) h.up[kind].o.mb = p.maxBytes / 1048576;
    }
  }
  /** Пресеты фонового звука персонажа (GET /app/sfx-presets): в SFX на месте, слот карточки — заново */
  function sfxFor(f: Frame) {
    const form = f.svc.get().form;
    if (!form || !circleKeys(form).sfx) return;
    const charId = h.who() === 'char' ? num(h.char().id) : null;
    const key = `${charId ?? 0}:${locale()}`;
    const policy = circlePolicy(form);
    const max = Math.min(policy?.sfx_max_length ?? 120, toolField(form, circleKeys(form).sfx!)?.max_length ?? 120);
    const put = () => {
      const list = sfxPresets.get(key) ?? [];
      const next = list.map(([label, prompt]) => [label, Array.from(prompt).slice(0, max).join('')] as [string, string]);
      if (JSON.stringify(next) === JSON.stringify(h.lists.SFX)) return;
      h.lists.SFX.splice(0, h.lists.SFX.length, ...next);
      if (onForm() && cur() === f) h.syncSet();
    };
    if (sfxPresets.has(key)) return put();
    if (sfxFlight.has(key)) return;
    sfxFlight.set(
      key,
      endpoints.avatarCatalog
        .sfx(charId ?? undefined, locale())
        .then(
          value => {
            sfxPresets.set(key, (value?.items ?? []).map(item => [item.label, item.prompt] as [string, string]));
          },
          () => {
            sfxPresets.set(key, null);
          },
        )
        .finally(() => {
          sfxFlight.delete(key);
          if (cur() === f) put();
        }),
    );
  }
  /** Карточка персонажа, который говорит: виды (SKINS на месте), голос по умолчанию, голоса персонажа */
  function charFor(f: Frame) {
    if (h.who() !== 'char') return;
    const id = num(h.char().id);
    if (!id) return;
    const apply = (card: AvatarDetails | null) => {
      const skins: Array<[string, string, string]> = [[SKIN0[0], uiText(SKIN0[1]), ''], ...(card?.avatar.skins ?? []).map(sk => [String(sk.id), sk.name, ''] as [string, string, string])];
      const was = JSON.stringify(h.lists.SKINS);
      h.lists.SKINS.splice(0, h.lists.SKINS.length, ...skins);
      if (!skins.some(sk => sk[0] === S.skin)) S.skin = 'def';
      if (card) leaseSkins(card, skinId => skinReady(skinId));
      if (cur() !== f || !onForm()) return;
      const skinsChanged = was !== JSON.stringify(h.lists.SKINS);
      if (skinsChanged) h.syncSet();
      /* голос персонажа по умолчанию — когда у текста голоса ещё нет */
      if (h.speechText() && S.voice == null) {
        S.voice = charVoiceId();
        if (S.voice != null) h.voiceChanged();
      } else if (card && S.voice != null && card.avatar.voice_ids.includes(Number(S.voice)) && !skinsChanged) {
        /* голос персонажа уже выбран по ряду, но его строки не было до карточки — слот «Голос» стоял «Выберите голос»: перерисовать с именем */
        h.syncSet();
      }
    };
    const key = detailsKey(id);
    if (details.has(key)) return apply(details.get(key) ?? null);
    void loadDetails(id).then(card => {
      if (cur() === f && num(h.char().id) === id) apply(card);
    });
  }
  /** Картинка вида пришла: главная рамка (вид выбран) и карточка «Ещё настройки» на месте */
  function skinReady(skinId: number) {
    if (onForm() && String(S.skin) === String(skinId)) h.putMain(false);
    if (onForm()) h.syncSet();
  }
  /** Превью загрузки повтора по виду: фото и запись — с сервера; видео сервер не отдаёт (Q7) — мост просит загрузить заново */
  function restore(f: Frame, kind: Kind, id: string) {
    const key = keyOf(f, kind);
    if (!key || f.restoring[kind] === id || !f.svc.get().identity) return;
    f.restoring[kind] = id;
    void f.bridge.restore(key, id).finally(() => {
      if (f.restoring[kind] === id) delete f.restoring[kind];
      if (cur() === f && h.mounted()) formSync(f);
    });
  }
  /** Файлы в рамках — те, что у входов формы: прежний ушёл — рамка пустеет; id есть, файла нет — превью */
  function files(f: Frame) {
    const inputs = f.svc.get().inputs;
    const who = h.who();
    for (const kind of ['photo', 'video', 'audio'] as const) {
      const key = keyOf(f, kind);
      if (!key) continue;
      const id = inputs[key];
      const item = f.items[kind];
      const mine = kind === 'audio' ? h.speechAud() : who === kind;
      /* файл того, кто говорит, без загрузки в форме (мост снял её — отказ, превью не открылось) */
      if (mine && item && !f.bridge.item(key) && !id) dropFile(f, kind);
      if (mine && id && !held(kind) && !f.restoring[kind] && !f.bridge.item(key)) restore(f, kind, id);
    }
  }
  const held = (kind: Kind) => (kind === 'audio' ? !!S.aud : !!S.files[kind]);
  function dropFile(f: Frame, kind: Kind) {
    const file = kind === 'audio' ? S.aud : S.files[kind];
    if (file) revoke(f, file);
    delete f.items[kind];
    if (kind === 'audio') {
      S.aud = null;
      if (onForm()) h.audPaint();
    } else {
      S.files[kind] = null;
      if (onForm()) h.fileChanged();
    }
  }
  function revoke(f: Frame, file: EngineFile) {
    for (const url of [file.url, file.video, file.poster])
      if (typeof url === 'string' && f.urls.delete(url))
        try {
          URL.revokeObjectURL(url);
        } catch {
          /* адрес уже освобождён */
        }
  }
  function formSync(f: Frame) {
    const { s, d } = live(f);
    const form = s.form;
    if (form && !f.form && f.formAt !== form) {
      /* первая форма: сервис ставит форму, затем её входы (подстановка входа и повтора) отдельными шагами одного прохода
         (order-service form) — сверка после всех шагов, иначе подстановка придёт поверх зеркала как чужая правка и вход экрана
         («Видео говорит»: новые слова, формат как в ролике) потеряется */
      if (!f.formWait) {
        f.formWait = true;
        queueMicrotask(() => {
          f.formWait = false;
          f.formAt = f.svc.get().form;
          changed(f);
        });
      }
      return;
    }
    if (form && f.form !== form) {
      const first = !f.form;
      f.form = form;
      const was = listsKey();
      lists(f, form);
      if (first) {
        /* первая форма кадра: входы (повтор, заявка родной формы, значения по умолчанию) — в S, вход экрана решает своё сам */
        adopt(f, s.inputs, { who: f.keepWho, format: f.keepFormat, speech: f.keepSpeech }, true);
        f.prefill = f.known;
        if (onForm() && !swapping()) h.paintPage();
      } else if (onForm() && !swapping() && was !== listsKey()) h.paintPage(); /* та же форма заново (возврат к форме, новая ревизия): только если списки сменились */
    }
    if (!form) return;
    /* заявка, входы которой S не знает (перезагрузка, заявка родной формы): её вид — для этапов и снимка хвоста */
    const it = intentOf(f);
    if (it && f.intentSeen !== it.key) {
      f.intentSeen = it.key;
      const inputs = (it.body.inputs ?? {}) as Inputs;
      if (!f.known || !sameInputs(strip(f, inputs), f.known)) adopt(f, inputs);
    }
    /* входы сменились не зеркалом (повтор, «Изменить» после перезагрузки): вид в S и форма заново */
    if (!d.locked && f.known && !sameInputs(strip(f, s.inputs), f.known)) {
      if (f.prefill && sameInputs(strip(f, s.inputs), f.prefill)) {
        /* сервис перечитал форму (пришёл аватар, «Обновить форму») и снова поставил ту же подстановку: S уже её знает и правил
           поверх неё — S снова во входы (commit ниже), вход экрана и правки человека не теряются */
        f.known = strip(f, s.inputs);
        f.mirror.seen(s.inputs);
      } else {
        adopt(f, s.inputs);
        if (onForm() && !swapping()) h.paintPage();
      }
    }
    if (!onForm()) return;
    files(f);
    charFor(f);
    sfxFor(f);
    /* ошибка формы (не загрузилась, файлы повтора недоступны) — один раз в рамке того, кто говорит */
    const error = s.integrityError ? '' : s.error;
    if (error !== f.said) {
      f.said = error;
      if (FORM_SAY.includes(error)) frameOf()?.say(uiText(error));
    }
    commit(f);
    h.foot.sync();
  }
  /** Рамка загрузки того, кто говорит (или записи речи) */
  function frameOf(): EngineUpload | null {
    const who = h.who();
    if (h.speechAud()) return h.up.audio;
    return who === 'photo' || who === 'video' ? h.up[who] : null;
  }
  function changed(f: Frame) {
    if (cur() !== f || !h.mounted()) return;
    formSync(f);
    f.bridge.sync();
    f.driver.sync();
    f.consent?.refresh();
    balance(f);
  }
  /** Остаток для «Не хватает Circle» — тот же, что у родной кнопки (useCircleBalance, GET /app/balance, память на минуту) */
  function balance(f: Frame) {
    const orderId = f.svc.derive().orderId;
    if (orderId && orderId !== f.orderSeen) {
      f.orderSeen = orderId;
      forgetCircleBalance();
    }
    const shown = onForm(),
      fresh = shown && !f.formShown;
    f.formShown = shown;
    if (!fresh || f.balanceAsk || circleBalance() !== null || f.svc.get().form?.chargeable === false) return;
    f.balanceAsk = true;
    const seen = f.orderSeen;
    void endpoints
      .balance()
      .then(
        value => {
          if (f.orderSeen === seen) rememberCircleBalance(value?.circle);
        },
        () => {
          /* ошибка — проверки нет, решает сервер (402), как у родной кнопки */
        },
      )
      .finally(() => {
        f.balanceAsk = false;
        if (cur() === f && onForm()) h.foot.sync();
      });
  }

  /* ---- хвост BotRun: фасад одного экземпляра на кадр ---- */
  function rebuild() {
    if (!tailOpts) return;
    const { create, opts } = tailOpts;
    const base = typeof opts.missing === 'function' ? (opts.missing as () => string) : () => '';
    const T = (opts.T as Record<string, unknown>) ?? {};
    const baseDone = T.doneP;
    const baseNext = typeof opts.next === 'function' ? (opts.next as (r: BotRunRun) => string[][]) : null;
    inst = create({
      ...opts,
      drive: (r: BotRunRun) => server.drive(r),
      failText: (r: BotRunRun) => server.failText(r),
      receipt: (r: BotRunRun) => server.receipt(r),
      /* подвал отказов сервера и «Открыть в чате» экрана (data-app-chat: заказ просмотрен, в Telegram — закрыть приложение) */
      foot: chatFoot(serverFoot((opts.foot as Record<string, unknown>) ?? {}, 'app'), 'app'),
      /* подпись итога по доставке заказа: бот не присылает — «Результат сохранён в «Истории»…» и ссылка «Настройки» */
      T: {
        ...T,
        doneP: (r: BotRunRun) => {
          const base = typeof baseDone === 'function' ? String((baseDone as (r: BotRunRun) => unknown)(r)) : String(baseDone ?? '');
          const f = cur() ?? frame;
          if (f) posterFill(f);
          return doneP(f?.svc.get().order, base, 'app');
        },
      },
      /* «Что дальше»: «Сделать видео» и «Сделать кружок» — только с опубликованным форматом (родной хвост: тот же вход, другой формат) */
      ...(baseNext ? { next: (r: BotRunRun) => baseNext(r).filter(([attr]) => nextOn(attr)) } : {}),
      /* «Скачать» и «Ещё вариант» итога (res шаблона) — подписи словарём профиля в момент показа (BotRun.acts берёт подписи ядра как есть) */
      ...(opts.res
        ? {
            acts: () => [
              ['data-run-save', 'download', uiText('Скачать')],
              ['data-run-again', 'refresh', uiText('Ещё вариант')],
            ],
          }
        : {}),
      steps: () => server.steps(),
      snap: () => server.snap(),
      price: () => server.sentPrice(),
      /* повтор заявки и «Попробовать ещё раз» решает сервис: форма в памяти им не нужна */
      missing: () => {
        const f = cur();
        if (f) {
          const { s, d } = live(f);
          if (s.intent || d.orderId) return '';
        }
        return base();
      },
    });
    if (!Object.hasOwn(facade, 'drop')) {
      /* смена страницы start (SteosUI.swap) рисует заказ через 170 мс: пока она не прошла, итог не закрывается (drop обнулил бы RUN, и
         отрисовка start упала бы на RUN.st; итог, которого человек не видел, откроется при следующем входе) */
      facade.start = () => {
        inst?.start();
        if (!inst?.run) return;
        startSwap = true;
        window.setTimeout(() => {
          startSwap = false;
        }, 250);
      };
      facade.drop = () => {
        if (!startSwap) inst?.drop();
      };
    }
    for (const key of Object.keys(inst)) {
      if (key === 'run' || Object.hasOwn(facade, key)) continue;
      (facade as unknown as Record<string, unknown>)[key] = (...args: unknown[]) => (inst as unknown as Record<string, (...a: unknown[]) => unknown>)[key](...args);
    }
  }
  Object.defineProperty(facade, 'run', { enumerable: true, get: () => inst?.run ?? null });
  const baseSnap = () => (tailOpts?.opts.snap as (() => Record<string, unknown>) | undefined)?.() ?? {};
  const baseSteps = () => (tailOpts?.opts.steps as (() => string[][]) | undefined)?.() ?? h.runSteps();

  /* ---- привязка кадра ---- */
  /** «Вы» и близкие сменились (server-state, листы людей): ряд лиц на месте, кнопка и входы — заново. Слушатель — с первой привязки:
      при установке движка в BotPeople ещё демо-люди шаблона без фото (их снимает server-state) */
  let watching = false;
  function watch() {
    if (watching) return;
    watching = true;
    watchPeople({
      paint: () => h.P.paintForm(),
      mounted: () => !!cur() && onForm(),
      changed: () => {
        commit();
        h.foot.sync();
      },
      m: safePeople,
    });
  }
  function bind(b: EngineBinding): EngineBound {
    watch();
    if (!frame || frame.key !== b.key || frame.svc !== b.svc) {
      if (frame) dropFrame(frame);
      frame = makeFrame(b);
    } else frame.b = b;
    const mine = frame;
    bound = mine;
    return {
      changed: () => changed(mine),
      release() {
        if (bound === mine) bound = null;
      },
    };
  }
  function dropFrame(f: Frame) {
    f.media.release();
    f.bridge.cancel();
    f.consent?.close();
    f.consent = null;
    f.pick.abort();
    for (const url of f.urls)
      try {
        URL.revokeObjectURL(url);
      } catch {
        /* адрес уже освобождён */
      }
    f.urls.clear();
  }
  function makeFrame(b: EngineBinding): Frame {
    const f = {} as Frame;
    const guard =
      <A extends unknown[]>(fn: (...args: A) => void) =>
      (...args: A) => {
        if (frame === f) fn(...args);
      };
    const opts = circleEntry(b.app, b.initialInputs, !!b.templateEntry);
    Object.assign(f, {
      b,
      key: b.key,
      svc: b.svc,
      app: b.app,
      mirror: createInputsMirror(b.svc),
      form: null,
      known: null,
      entry: {},
      keepWho: !!opts.entryWho && ['photo', 'video', 'audio'].includes(opts.entryWho),
      keepFormat: !!opts.preset?.template || !!opts.entryFormat,
      keepSpeech: false,
      intentSeen: '',
      formWait: false,
      formAt: null,
      prefill: null,
      approved: new WeakSet<File>(),
      items: {},
      urls: new Set<string>(),
      restoring: {},
      pending: {},
      said: '',
      consent: null,
      pick: createVoicePick(),
      voices: new Map(),
      peek: null,
      formShown: false,
      balanceAsk: false,
      orderSeen: undefined,
      rec: 0,
      media: createResultMedia({ svc: b.svc, ic: (name: string) => h.ic(name) }),
    });
    f.entry = entryOf(b, opts);
    /* речь решил вход: новые слова или своя речь ролика, текст или запись, открытка и шаблон — текстом */
    f.keepSpeech = ['words', 'drive', 'card', 'app'].some(key => f.entry[key] !== undefined);
    f.bridge = createMediaBridge({
      svc: b.svc,
      owner: () => f.svc.get().identity?.owner,
      say: guard((text: string, key: string) => {
        const kind = kindOfKey(f, key);
        (kind ? h.up[kind] : frameOf())?.say(text);
      }),
      put: guard((item: MediaItem) => {
        const kind = item.kind as Kind;
        if (!item.file) return;
        f.items[kind] = item;
        f.approved.add(item.file);
        if (item.rec) f.rec++;
        /* файл читает шаблон (кадр, длительность, обложка, волна) — так же, как в демо */
        if (kind === 'audio') h.readAud(item.file);
        else h.readFile(item.file, kind);
      }),
      drop: guard((item: MediaItem | null, key: string) => {
        const kind = kindOfKey(f, key);
        if (!kind || (item && f.items[kind] !== item)) return;
        dropFile(f, kind);
        commit(f);
      }),
      update: guard((item: MediaItem) => {
        const kind = item.kind as Kind;
        if (f.items[kind] !== item) return;
        const file = kind === 'audio' ? S.aud : S.files[kind];
        if (file && item.check) {
          /* проверки сервера: лица не видно, звука нет */
          const noface = checkFailed(item.check, 'face');
          const mute = item.check.media?.hasAudio === false;
          if (!!file.noface !== noface || !!file.mute !== mute) {
            file.noface = noface;
            file.mute = mute;
            if (onForm()) (kind === 'audio' ? h.audPaint : h.fileChanged)();
          }
        }
        commit(f);
        if (onForm()) h.foot.sync();
      }),
    });
    f.driver = createRunDriver({
      svc: b.svc,
      tail: () => h.tail,
      root: () => h.root(),
      mounted: () => cur() === f && h.mounted(),
      localSteps: () => server.steps(),
      paintForm: () => {
        h.whoSync();
        h.paintPage();
      },
      tip: (el, text) => tip(el, text),
    });
    return f;
  }
  /** Вход экрана в контракт apply шаблона: «Кружок из аудио», «Видеооткрытка», шаблон формата «кружок» (название, формат закреплён),
      кто говорит, чем, формат, сразу «Другой голос» (circle-entry, как у родной формы) */
  function entryOf(b: EngineBinding, opts: ReturnType<typeof circleEntry>): Record<string, unknown> {
    const init = b.initialInputs ?? {};
    const entry = (b.app.entry ?? {}) as Record<string, unknown>;
    const o: Record<string, unknown> = {};
    const who = opts.preset?.audio ? 'audio' : opts.entryWho;
    if (opts.preset?.template) o.app = { id: b.app.id, n: b.app.name, k: 1, f: 'circle' };
    if (who) o.who = who;
    /* входы плиток приходят строками (studio-navigation: options — String(значение)): card и voicePick — 'true' */
    const flag = (key: string) => entry[key] === true || init[key] === true || init[key] === 'true';
    if (opts.preset?.card || flag('card')) {
      o.card = true;
      o.who ??= 'char';
    }
    const drive = entry.drive ?? init.drive;
    if (drive === 'text' || drive === 'audio') o.drive = drive;
    const words = entry.words ?? init.words;
    if (words === 'own' || words === 'new') o.words = words;
    if (opts.entryFormat && opts.entryFormat !== 'video') o.fmt = opts.entryFormat;
    if (opts.voicePick || flag('voicePick')) o.voicePick = true;
    return o;
  }

  const server: CircleServer = {
    ui: base =>
      serverUi(base, {
        poorBalance: () => {
          const f = cur();
          /* кнопка чинит («Обновить форму», «Продолжить загрузку»), а не создаёт: «Не хватает · Пополнить» её не заслоняет */
          if (!f || recovery(f)) return Infinity;
          const s = f.svc.get();
          return poorBalance({ balance: circleBalance(), form: s.form, intent: s.intent }, true);
        },
      }),
    blank: () => SILHOUETTE,
    entry() {
      return cur()?.entry ?? {};
    },
    open(o) {
      const f = cur();
      if (!f) {
        /* без привязки (кадр не активен) — форма без действий: missing() держит «Проверяем доступность» */
        h.setup(o);
        h.paintPage();
        return;
      }
      const fresh = painted !== f;
      if (fresh) {
        /* другой кадр или новый сервис под прежним ключом: S, файлы, голос и хвост прежнего сюда не переходят */
        h.stopLive();
        for (const [key, value] of Object.entries(JSON.parse(S0) as Record<string, unknown>)) (S as Record<string, unknown>)[key] = value;
        h.P.setIn(false);
        rebuild();
        painted = f;
        f.form = null;
        f.known = null;
        f.intentSeen = '';
      }
      f.formShown = false;
      const root = h.root();
      if (h.tail.run && root) {
        h.tail.show(root);
        changed(f);
        return;
      }
      /* вход экрана — один раз на кадр: новое открытие того же кадра правки человека не сбрасывает */
      if (fresh) h.setup(o);
      f.peek = peekIntent(f.app.id);
      if (f.driver.first(f.peek) === 'form' && root) root.scrollTop = 0;
      changed(f);
    },
    close() {
      const f = frame;
      if (!f) return;
      f.consent?.close();
      f.consent = null;
      f.pick.abort();
      f.media.stop();
    },
    tail(create, opts) {
      tailOpts = { create, opts };
      rebuild();
      return facade;
    },
    wrap<T>(_slot: 'cheap', value: T): T {
      const cheap = value as unknown as { go?: unknown; where?: unknown };
      const offer = () => {
        const f = cur();
        if (!f) return null;
        const { s, d } = live(f);
        return studioCheapOfferAt(s.form, d.price);
      };
      /* скидку «Сделать дешевле» публикует форма (Q5): без неё у подвала нет ни цены, ни действия скидки; цена — формы, не шаблона */
      return {
        where: cheap.where,
        get price() {
          const o = offer();
          return o ? () => o.price : undefined;
        },
        get go() {
          return offer() ? cheap.go : undefined;
        },
        get percent() {
          return offer()?.percent;
        },
      } as unknown as T;
    },
    has(part) {
      const f = cur() ?? frame;
      const form = f?.svc.get().form;
      if (part === 'sty') return false;
      if (!form) return part !== 'skin';
      if (part === 'char' || part === 'photo' || part === 'video') return circleSources(form)[part];
      if (part === 'skin') return !!circleKeys(form).skin && h.lists.SKINS.length > 1;
      /* кто говорит — только здесь: ряд лиц спрашивает has('char') изнутри who() (BotPeople conf) */
      const who = h.who();
      const sp = speechOf({ who, speech: S.speech === 'audio' ? 'audio' : 'text', keep: h.keepOn() });
      switch (part) {
        case 'loop':
          return circleLoopOn(form, who, sp);
        case 'motion':
          return circleMotionOn(form, who);
        case 'sfx':
          return circleSfxOn(form, who, sp);
      }
      return true;
    },
    missing,
    ready,
    label() {
      const f = cur();
      const act = uiText(h.round() ? 'Создать кружок' : 'Создать видео');
      if (!f) return act;
      const { s, d } = live(f);
      if (s.intent && !d.orderId) return uiText('Продолжить проверку');
      if ((!s.form && s.error && !s.integrityError) || s.versionStale) return uiText('Обновить форму');
      if (f.bridge.stalled() && !s.busy && s.error === TRANSPORT) return uiText('Продолжить загрузку');
      if (!s.form || s.form.chargeable === false || !Number.isFinite(d.price)) return act;
      return null;
    },
    price(cheap) {
      const f = cur();
      const s = f?.svc.get();
      if (!f || !s?.form) return 0;
      const base = f.svc.derive(s).price;
      if (cheap) {
        const offer = studioCheapOfferAt(s.form, base);
        if (offer) return offer.price;
      }
      return base;
    },
    busy() {
      const f = cur();
      const s = f?.svc.get();
      return !!s && s.busy && !s.uploadingFile;
    },
    unit(round) {
      const n = unitPrice();
      return n ? core().html.unit(n, unitText(round)) : '';
    },
    badge(round, o) {
      const n = unitPrice();
      return n ? core().priceBadge(n, unitText(round), o) : '';
    },
    upOpts: kind => ({
      accept: ACCEPT[kind],
      /* свои строки рамки BotCore.upload («Загрузить», «Снять», «Заменить», «Убрать», отказы) — словарём профиля */
      tx: (template: string, ...values: unknown[]) => uiFormat(template, ...values),
      ...(kind === 'photo' ? { note: () => server.note('photo') } : {}),
    }),
    note(kind) {
      const f = cur() ?? frame;
      const key = f ? keyOf(f, kind) : undefined;
      const policy = f && key ? mediaPolicy(f.svc.get().form, key) : null;
      if (policy) return policy.note;
      return uiText(kind === 'photo' ? 'JPG или PNG, до 10 МБ' : kind === 'video' ? 'MP4 или MOV, до 200 МБ' : 'MP3, WAV или M4A, до 50 МБ');
    },
    admit(file, kind) {
      const f = cur();
      if (!f) return false;
      if (f.approved.has(file)) {
        f.approved.delete(file);
        return true;
      }
      const key = keyOf(f, kind);
      if (!key) {
        h.up[kind].say(uiText('Этот инструмент сейчас недоступен. Попробуйте обновить форму.'));
        return false;
      }
      const policy = mediaPolicy(f.svc.get().form, key);
      if (!policy) return false;
      void f.bridge.pick(key, file);
      return false;
    },
    accept(file, kind) {
      const f = cur();
      if (!f) return;
      for (const url of [file.url, file.video]) if (typeof url === 'string' && url.startsWith('blob:')) f.urls.add(url);
      /* прежний файл вида уходит из памяти (новый выбор его заменил) */
      const was = kind === 'audio' ? S.aud : S.files[kind];
      if (was && was !== file) revoke(f, was);
      const item = f.items[kind];
      if (item?.rec) file.name = uiFormat('Запись {0}', f.rec);
      if (kind !== 'audio' && item?.restored) placeOn(f, kind, file);
      if (item?.check) {
        file.noface = checkFailed(item.check, 'face');
        file.mute = item.check.media?.hasAudio === false;
      }
    },
    remove(kind) {
      const f = cur();
      if (!f) return;
      const key = keyOf(f, kind);
      const file = kind === 'audio' ? S.aud : S.files[kind];
      if (file) revoke(f, file);
      delete f.items[kind];
      delete f.restoring[kind];
      if (key) f.bridge.remove(key);
    },
    canRecord: () => !!(window.SteosUI as unknown as { canRecord?(): boolean }).canRecord?.(),
    micText: kind => uiText(kind === 'off' ? 'Запись здесь недоступна, загрузите файл' : 'Не удалось включить микрофон. Разрешите доступ или загрузите файл.'),
    camText: () => uiText('Камера недоступна. Загрузите файл.'),
    recorded({ blob, url }) {
      if (url)
        try {
          URL.revokeObjectURL(url);
        } catch {
          /* адрес записи больше не нужен: мост делает свой WAV */
        }
      h.audPaint();
      const f = cur();
      const key = f ? keyOf(f, 'audio') : undefined;
      if (!f || !key) return;
      if (!blob) {
        h.up.audio.say(uiText('Не удалось прочитать запись, попробуйте ещё раз'));
        return;
      }
      const policy = mediaPolicy(f.svc.get().form, key);
      void f.bridge.recorded(key, blob, policy?.maxMs ?? h.limits().rec * 1000);
    },
    voice,
    defVoice: () => charVoiceId(),
    charS2s() {
      const id = charVoiceId();
      return id !== null && voice(id)?.is_s2s === true;
    },
    /* надбавку за смену голоса записи сервер не публикует: строки цены в подписи секции нет */
    voiceHint: () => '',
    pickVoice(id, c) {
      const f = cur();
      const want = num(id);
      const seq = ++pickSeq;
      if (!f || !h.mounted() || h.tail.run || want === null || f.svc.derive().locked) return Promise.resolve(false);
      const s2s = !!c;
      return f.pick
        .pick(want, { s2s })
        .then(picked => {
          /* ответ на прежнее нажатие после нового — не применяется */
          if (seq !== pickSeq || !picked || cur() !== f || f.svc.derive().locked) return false;
          if (!picked.ok) {
            tip(h.root()?.querySelector('#apSet') ?? null, 'Этот голос не умеет переозвучку, выберите другой');
            return false;
          }
          f.voices.set(picked.voice.id, engineVoice(picked.voice));
          if (!s2s) f.svc.setters.voice(picked.voice);
          S.voice = picked.voice.id;
          if (c) S.vm[c] = 'voice';
          h.voiceChanged();
          h.ui().buzz();
          return true;
        })
        .catch(() => {
          if (seq === pickSeq) tip(h.root()?.querySelector('#apSet') ?? null, 'Не удалось выбрать голос. Попробуйте ещё раз.');
          return false;
        });
    },
    dropPick() {
      pickSeq++;
    },
    charImg(c, skin) {
      const asset = (window as unknown as { ASSET?: (f: string) => string }).ASSET;
      const base = c.img && asset ? asset(c.img) : '';
      const id = num(c.id);
      if (!skin || skin[0] === 'def' || !id) return base;
      const card = details.get(detailsKey(id));
      return (card && skinUrl(card, skin[0])) || base;
    },
    meUrl: () => selfPicture(),
    commit: () => commit(),
    start(btn) {
      const f = cur();
      if (!f) return;
      commit(f);
      const { s, d } = live(f);
      if (s.intent && !d.orderId) {
        h.tail.start();
        return;
      }
      const fix = recovery(f);
      if (fix) {
        fix(btn);
        return;
      }
      /* файлы есть, а права на них не подтверждены (повтор): лист согласия, затем создание */
      if (!s.rights && s.form && Object.entries(s.inputs).some(([key, value]) => value && mediaPolicy(s.form, key))) {
        f.consent = openConsentSheet({
          rights: () => f.svc.get().rights,
          locked: () => f.svc.derive().locked,
          setRights: on => f.svc.setters.rights(on),
          onContinue: () => {
            f.consent = null;
            if (cur() === f && onForm()) server.start(btn);
          },
          onClose: () => {
            f.consent = null;
          },
        });
        return;
      }
      if (h.foot.poor()) {
        h.foot.poorOpen();
        return;
      }
      h.tail.start();
    },
    steps: () => baseSteps(),
    snap() {
      const f = cur();
      const it = intentOf(f);
      const sn = baseSnap();
      /* картинка и стиль — только то, что у сервера есть: стилей шаблона нет (стиль анимации «Кружка из аудио» — поле формы), картинка
         итога — превью, а не демо */
      return { ...sn, img: null, sty: S.app ? '' : (sn.sty ?? ''), cheap: !!(it ? it.body.cheap : S.cheap) };
    },
    sentPrice: () => cur()?.svc.derive().price ?? 0,
    drive(r) {
      const f = cur();
      if (f) void f.driver.drive(r);
    },
    failText(r) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const order = s?.order ?? null;
      const sn = r.snap as { out?: string; speech?: string };
      const circle = sn.out === 'circle';
      const steps = tailSteps(order);
      return failText(r, {
        order,
        error: s ? s.integrityError || s.error : '',
        texts: {
          modH: sn.speech === 'audio' ? 'Аудио не прошло проверку' : sn.speech === 'video' ? 'Видео не прошло проверку' : 'Текст не прошёл проверку',
          techH: circle ? 'Не получилось собрать кружок' : 'Не получилось собрать видео',
          notMade: uiText(circle ? 'кружок не создан' : 'видео не создано'),
        },
        step: steps.length ? steps[tailCurrent(order, steps)] : undefined,
      });
    },
    receipt(r) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const cheap = s?.intent ? !!s.intent.body.cheap : !!r.snap.cheap;
      return receipt(r, s?.order ?? null, { cheap, where: (r.snap as { out?: string }).out === 'circle' ? 'на кружке' : 'на видео' });
    },
    click(d, el, r) {
      const f = cur();
      const tailKeys = ['appRetry', 'appRchk', 'appFix', 'appChat', 'appSettings', 'runPlay', 'runVplay', 'runTovid', 'runTocirc', 'runVoice'];
      if (!tailKeys.some(key => d[key] !== undefined)) return false;
      if (!f) return true;
      const order = f.svc.get().order;
      const circle = (r?.snap as { out?: string } | undefined)?.out === 'circle';
      if (d.appChat !== undefined) {
        if (r?.st === 'done')
          chatClick({
            order,
            el,
            text: circle ? 'Тут откроется тема чата, куда бот прислал кружок' : 'Тут откроется тема чата, куда бот прислал видео',
            tip: (at, text) => h.ui().tip(at, text),
          });
        return true;
      }
      if (d.appSettings !== undefined) {
        openSettings();
        return true;
      }
      if (d.runPlay !== undefined || d.runVplay !== undefined) {
        if (r?.st === 'done') void play(f, el, r, d.runPlay !== undefined);
        return true;
      }
      if (f.driver.busy()) return true;
      if (d.appRetry !== undefined) h.tail.retry();
      else if (d.appRchk !== undefined) void f.driver.check(el);
      else if (d.runTovid !== undefined || d.runTocirc !== undefined) {
        /* «Сделать видео» / «Сделать кружок» (Q-C4, как родной хвост): к форме с тем же входом и другим форматом */
        const toVideo = d.runTovid !== undefined;
        exit(f, () => {
          if (toVideo) {
            S.fmt = 'video';
            S.ar = h.lists.FMT.some(item => item[0] === '9:16') ? '9:16' : S.ar;
            S.arUser = true;
          } else S.fmt = 'circle';
        });
      } else if (d.runVoice !== undefined)
        /* «Другой голос»: к форме с тем же входом и сразу выбор голоса в листе «Ещё настройки» */
        exit(f, null, () => h.voicePick());
      else exit(f);
      return true;
    },
    leave(go) {
      const f = cur();
      if (!f) return;
      f.media.stop();
      void f.driver.leave(go, false, '');
    },
    save(btn) {
      const f = cur();
      const index = f ? resultIndex(f) : null;
      if (!f || index === null || h.tail.run?.st !== 'done') return;
      f.media.stop(false);
      void core().saveBtn(btn, () => f.media.download(index), { buzz: () => h.ui().buzz(), tip: (el: Element, text: string) => h.ui().tip(el, text) });
    },
  };

  /* ---- итог и действия хвоста ---- */
  /** Индекс файла итога: первое видео заказа (кружок или ролик), иначе первый файл; заказ не готов — null */
  function resultIndex(f: Frame): number | null {
    const order = f.svc.get().order;
    if (order?.status !== 'done' || !order.result?.available) return null;
    const assets = order.result.assets;
    return (assets.find(asset => asset.mime.startsWith('video/')) ?? assets[0])?.index ?? null;
  }
  /** Кадр из готового ролика (GET …/result/files/{i}/poster) в рамке итога: снимок формы (фото исполнителя) после перезагрузки или с другого
      устройства пуст, и кружок стоял чёрным кругом с кнопкой. Кадр грузится с авторизацией один раз на файл и ставится фоном рамки после отрисовки */
  const posters = new Map<string, string | null>();
  function posterFill(f: Frame) {
    const order = f.svc.get().order;
    const index = resultIndex(f);
    if (!order || index === null) return;
    const key = `${order.id}:${order.result_revision ?? 1}:${index}`;
    const put = (url: string) =>
      requestAnimationFrame(() => {
        if (cur() !== f) return;
        h.root()
          ?.querySelectorAll<HTMLElement>(`${RESULT_FRAMES.circle.slot}, ${RESULT_FRAMES.frame.button} ${RESULT_FRAMES.frame.slot}`)
          .forEach(el => {
            /* идёт показ — <video> в рамке не трогаем */
            if (el.querySelector('video')) return;
            el.innerHTML = `<img src="${url}" alt="" style="width:100%;height:100%;object-fit:cover;display:block">`;
          });
      });
    if (posters.has(key)) {
      const url = posters.get(key);
      if (url) put(url);
      return;
    }
    posters.set(key, null);
    void endpoints.history
      .download(order.id, `/app/orders/${order.id}/result/files/${index}/poster?revision=${order.result_revision ?? 1}`)
      .then(blob => {
        if (!blob.type.startsWith('image/')) return;
        const url = URL.createObjectURL(blob);
        posters.set(key, url);
        put(url);
      })
      .catch(() => {
        /* кадра нет (старый заказ, сбой) — остаётся снимок формы */
      });
  }
  /** Нажатие на итог: настоящий файл с сервера в рамке шаблона (кружок — .ar-pic, ролик — .pr-vf), по нажатию, без автозагрузки */
  async function play(f: Frame, el: HTMLElement, r: BotRunRun, circle: boolean) {
    const index = resultIndex(f);
    if (index === null) return;
    const frameOf = circle ? RESULT_FRAMES.circle : RESULT_FRAMES.frame;
    const button = el.closest<HTMLElement>(frameOf.button) ?? h.root()?.querySelector<HTMLElement>(frameOf.button) ?? el;
    const poster = (f.svc.get().order?.result as { poster_url?: unknown } | null | undefined)?.poster_url;
    const ok = await f.media.toggle(index, { el: button, kind: frameOf.kind, dur: Number((r.snap as { dur?: unknown }).dur) || null, poster });
    if (!ok && cur() === f) {
      const error = f.svc.get().error;
      tip(button, error || 'Файл результата пока недоступен. Проверьте работу в «Истории».');
    }
  }
  /** Назад к форме с тем же входом (run-driver.leave: заказ просмотрен, new key только после свежего GET): prep — до отрисовки формы, after — после */
  function exit(f: Frame, prep?: (() => void) | null, after?: () => void) {
    f.media.stop();
    void f.driver.leave(
      () =>
        h.tail.exit(() => {
          prep?.();
          h.whoSync();
          h.paintPage();
          after?.();
        }),
      false,
      '',
    );
  }
  /** Действие «Что дальше» доступно: формат результата опубликован */
  function nextOn(attr: string): boolean {
    const form = (cur() ?? frame)?.svc.get().form;
    if (!form || (attr !== 'data-run-tovid' && attr !== 'data-run-tocirc')) return true;
    const values = circleFormatChoices(form, '').map(item => item[0]);
    return attr === 'data-run-tovid' ? values.includes('9:16') : values.includes('circle');
  }

  /* ---- смена языка профиля, ряд лиц, остаток ---- */
  /** Подписи этапов идущего заказа — строки на момент создания хвоста: им — подписи нового языка (ключи этапов те же) */
  function relabel(r: BotRunRun) {
    const fresh = server.steps();
    for (const step of r.steps) {
      const same = fresh.find(item => item[0] === step[0]);
      if (same) {
        step[2] = same[2];
        step[3] = same[3];
      }
    }
  }
  /** Открытый экран заново на новом языке: хвост — TAIL.show (ход итога на месте), форма — paintPage; затем обычная сверка */
  function relocale() {
    const r = h.tail.run;
    if (r) relabel(r);
    const f = cur();
    const root = h.root();
    if (!f || !root || !h.mounted()) return;
    if (h.tail.run) h.tail.show(root);
    else h.paintPage();
    changed(f);
  }
  document.addEventListener('steos:locale', relocale);
  /* новый снимок Me уже положил остаток в память кнопки (tool-helpers): подвал формы — по нему */
  document.addEventListener('steos:account-updated', () => {
    if (cur() && onForm()) h.foot.sync();
  });
  instance = { bind };
  return server;
}

/** BotPeople для подписи ряда лиц: у записи без фото (демо-люди шаблона до server-state) — пустой список фото */
function safePeople(): PeopleModule | null {
  const m = peopleModule();
  if (!m) return null;
  return { ...m, store: { all: () => m.store.all().map(p => ({ ...p, photos: Array.isArray(p.photos) ? p.photos : [] })) } };
}

/** ENGINES.who.bind: привязка кадра к серверу экрана, созданному движком (без движка в серверном режиме — пустая привязка) */
export function bindCircleEngine(b: EngineBinding): EngineBound {
  return instance ? instance.bind(b) : { changed() {}, release() {} };
}

/** Вид знака заказа: кружок или видео по формату заказа (как родной хвост: circle | lipsync) */
export function circleKind(s: { intent: Intent | null }): 'circle' | 'lipsync' {
  return s.intent?.body.inputs?.format === 'circle' ? 'circle' : 'lipsync';
}

/** Экран «who» реестра движков студии (circle, lipsync — псевдонимы canonicalScreen): движок шаблона window.BotApp, атрибуты data-app-*,
    правая панель; знак заказа — кружок или видео по формату */
export const CIRCLE_ENGINE: EngineScreen = { global: 'BotApp', prefix: 'app', side: true, kind: s => circleKind(s), bind: bindCircleEngine };

export function registerCircleEngine() {
  ENGINES.who = CIRCLE_ENGINE;
}
